/**
 * Social Trends Configuration Types
 *
 * TypeScript definitions for TrueTrends social media integration.
 * Used by the SocialTrendsConfigEditor component and API client.
 */

export interface SocialPlatformsConfig {
  twitter: boolean;
  tiktok: boolean;
  youtube: boolean;
  snapchat: boolean;
  instagram: boolean;
}

export interface CustomTopic {
  id: string;
  name: string;
  keywords: string[];
  enabled: boolean;
  created_at?: string;
}

export interface SocialTrendsConfig {
  social_trends_enabled: boolean;
  truetrends_api_url: string | null;
  truetrends_api_key_configured: boolean;
  social_sync_frequency_hours: number;
  social_data_retention_days: number;
  social_custom_topics: CustomTopic[];
  social_platforms_enabled: SocialPlatformsConfig;
  social_spike_threshold: number;
  social_spike_lookback_days: number;
}

export interface SocialTrendsConfigUpdate {
  social_trends_enabled?: boolean;
  truetrends_api_url?: string;
  truetrends_api_key_ref?: string;
  social_sync_frequency_hours?: number;
  social_data_retention_days?: number;
  social_platforms_enabled?: Partial<SocialPlatformsConfig>;
  social_spike_threshold?: number;
  social_spike_lookback_days?: number;
}

export interface CustomTopicCreate {
  name: string;
  keywords: string[];
  enabled?: boolean;
}

export interface TestConnectionResult {
  success: boolean;
  message: string;
  response_time_ms?: number;
  api_version?: string;
}

// Default values for new projects
export const DEFAULT_SOCIAL_CONFIG: SocialTrendsConfig = {
  social_trends_enabled: false,
  truetrends_api_url: null,
  truetrends_api_key_configured: false,
  social_sync_frequency_hours: 4,
  social_data_retention_days: 0,
  social_custom_topics: [],
  social_platforms_enabled: {
    twitter: true,
    tiktok: true,
    youtube: true,
    snapchat: false,
    instagram: false,
  },
  social_spike_threshold: 2.5,
  social_spike_lookback_days: 7,
};

// Platform metadata for UI display
export const SOCIAL_PLATFORMS = [
  { id: 'twitter' as const, label: 'Twitter/X', icon: '𝕏' },
  { id: 'tiktok' as const, label: 'TikTok', icon: '📱' },
  { id: 'youtube' as const, label: 'YouTube', icon: '▶️' },
  { id: 'snapchat' as const, label: 'Snapchat', icon: '👻' },
  { id: 'instagram' as const, label: 'Instagram', icon: '📷' },
] as const;

// Sync frequency options for UI display
export const SYNC_FREQUENCIES = [
  { value: 1, label: 'Every hour' },
  { value: 4, label: 'Every 4 hours (Recommended)' },
  { value: 12, label: 'Every 12 hours' },
  { value: 24, label: 'Daily' },
] as const;

// Data retention options for UI display
export const DATA_RETENTION_OPTIONS = [
  { value: 0, label: 'Keep forever' },
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
  { value: 180, label: '180 days' },
  { value: 365, label: '1 year' },
] as const;
